import { API } from "aws-amplify";

import { ClientPlatform } from "@/lib/models/assist";
import {
  PROOFREADING_CLIENT_PLATFORM,
  PostRuleBasedManuscriptParam,
  RuleBasedManuscriptSource,
} from "@/lib/api/proofreading";

/**
 * AI校正 API クライアント。
 * 仕様: `docs/proofreading/ai-frontend-spec.md` / nola-backend `tmp/proofreading/api-spec.md`
 */

export type AiProofreadingBatchStatus = "RUNNING" | "COMPLETED" | "FAILED" | "CANCELLED";

export type GeminiProofreadingExecution =
  | "batch"
  | "generateContent"
  | "generateContentParallel";

/**
 * 確認ダイアログ等の待ち時間コピー用。
 * BE の `PROOFREADING_EXECUTION_MODE_SWITCH` に合わせる。リクエストには載せない。
 */
export const AI_PROOFREADING_UI_EXECUTION_MODE: GeminiProofreadingExecution =
  "generateContentParallel";

export function isAiProofreadingSyncUi(
  mode: GeminiProofreadingExecution = AI_PROOFREADING_UI_EXECUTION_MODE,
): boolean {
  return mode !== "batch";
}

export type ProofreadingBatchCheckOptions = {
  errorCheck?: boolean;
  nounCheck?: boolean;
  storyCheck?: boolean;
  expressionCheck?: boolean;
  foreshadowCheck?: boolean;
  /** @deprecated storyCheck + expressionCheck に分割 */
  creativeCheck?: boolean;
};

export type ResolvedProofreadingBatchChecks = {
  errorCheck: boolean;
  nounCheck: boolean;
  storyCheck: boolean;
  expressionCheck: boolean;
  foreshadowCheck: boolean;
};

export type PostExtractProofreadingParams = {
  novelId: string;
  manuscripts: PostRuleBasedManuscriptParam[];
  checks?: ProofreadingBatchCheckOptions;
  platform?: ClientPlatform;
};

export type PostExtractProofreadingResponse = {
  status: "accepted";
  batchId: string;
  estimatedFish: number;
  checks: ResolvedProofreadingBatchChecks;
  planStructureSource: "theme";
  geminiExecution: GeminiProofreadingExecution;
};

export type GetProofreadingBatchResponse = {
  status: "ok";
  batchId: string;
  novelId: string;
  batchStatus: AiProofreadingBatchStatus;
  geminiExecution?: GeminiProofreadingExecution;
  usedFish?: number;
  usedRequestToken?: number;
  usedResponseToken?: number;
  createdAt?: number;
  updatedAt?: number;
};

/** AI校正指摘の重要度。info は参考情報で、high より下に並ぶ。 */
export type AiProofreadingPriority = "high" | "middle" | "low" | "info";

/* eslint-disable camelcase -- API レスポンス型はバックエンドの snake_case に合わせる */
export type AiProofreadingErrorEntry = {
  manuscript_id: string;
  /** 無い、または 1 未満のときは原稿単位の指摘として扱う。 */
  start_line?: number | null;
  start_word?: number | null;
  end_line?: number | null;
  end_word?: number | null;
  length?: number | null;
  target_text?: string | null;
  error_code: string;
  /** BE カタログの日本語分類。あればこのまま表示する。 */
  type?: string;
  priority: AiProofreadingPriority;
  fix_text: string | null;
  comment?: string;
};

export type AiProofreadingCheckResults = {
  results: AiProofreadingErrorEntry[];
};

export type ForeshadowCheckSource = "plan" | "manuscript_ending";
export type ForeshadowCheckAxis = "emotion" | "rule" | "both";

export type ForeshadowCheckReviewedEntry = {
  source: ForeshadowCheckSource;
  story_changes_heading: string;
  item: string;
  status: "ok" | "issue";
  axis: ForeshadowCheckAxis;
  plants_seen: string;
};

export type ForeshadowCheckEntry = {
  source: ForeshadowCheckSource;
  story_changes_heading: string;
  item: string;
  error_code: string;
  /** BE カタログの日本語分類。あればこのまま表示する。 */
  type?: string;
  priority: AiProofreadingPriority;
  axis: ForeshadowCheckAxis;
  payoff_manuscript_id: string;
  /** 無い、または 1 未満のときは原稿単位の指摘として扱う。 */
  payoff_start_line?: number | null;
  target_text?: string | null;
  plants_seen: string;
  comment: string;
};

export type AiProofreadingPositionedEntry = AiProofreadingErrorEntry & {
  start_line: number;
  start_word: number;
};

export type ForeshadowCheckPositionedEntry = ForeshadowCheckEntry & {
  payoff_start_line: number;
};

export type ForeshadowCheckResults = {
  /** BE のパース失敗や旧データも画面を落とさず扱うため、各配列の欠損を許容する。 */
  reviewed?: ForeshadowCheckReviewedEntry[];
  results?: ForeshadowCheckEntry[];
};

export type AiProofreadingResultPayload = {
  batchId: string;
  novelId: string;
  planStructureSource: "theme";
  checks: ResolvedProofreadingBatchChecks;
  result: {
    error_check?: AiProofreadingCheckResults;
    noun_check?: AiProofreadingCheckResults;
    story_check?: AiProofreadingCheckResults;
    expression_check?: AiProofreadingCheckResults;
    foreshadow_check?: ForeshadowCheckResults;
  };
};

export type FinishProofreadingBatchResponse = {
  status: "ok";
  batchId: string;
  novelId: string;
  batchStatus: AiProofreadingBatchStatus;
  alreadyCompleted?: boolean;
  skipped?: string;
  result?: AiProofreadingResultPayload;
};

export type CancelProofreadingBatchResponse = {
  status: "ok";
  batchId: string;
  novelId: string;
  batchStatus: "CANCELLED";
  alreadyCancelled: boolean;
  refundedFish: number;
};

export type CancelStaleProofreadingBatchResponse = CancelProofreadingBatchResponse & {
  elapsedMs: number;
};

export type EstimateAiProofreadingFishParams = {
  novelId: string;
  manuscripts: PostRuleBasedManuscriptParam[];
  checks?: ProofreadingBatchCheckOptions;
};

export type EstimateAiProofreadingFishResponse = {
  status: "ok";
  novelId: string;
  totalChars: number;
  estimatedFish: number | null;
  withinLimit: boolean;
  maxChars: number;
  enabledCheckCount?: number;
  sessionUsedFish: number;
  manuscripts: Array<{
    manuscript_key: string;
    source: RuleBasedManuscriptSource;
    chars: number;
  }>;
};

export type AiProofreadingIssueTypeEntry = {
  code: string;
  check: string;
  check_label: string;
  type: string;
};

export type GetProofreadingAiIssueTypesResponse = {
  lang: "ja";
  issue_types: AiProofreadingIssueTypeEntry[];
};

/**
 * AI 校正の error_code 日本語分類（`GET /proofreading/ai-issue-types`）。
 * 指摘カードは結果の `type` をそのまま出せば足りる。一覧が必要なときだけ使う。
 */
export async function getProofreadingAiIssueTypes(
  lang: "ja" = "ja",
): Promise<GetProofreadingAiIssueTypesResponse> {
  const response = (await API.get("NolaRestAPI", "/proofreading/ai-issue-types", {
    queryStringParameters: { lang },
  })) as GetProofreadingAiIssueTypesResponse | null;
  if (!response || !Array.isArray(response.issue_types)) {
    throw new Error("AI校正分類 API のレスポンス形式が不正です。");
  }
  return response;
}

/* eslint-enable camelcase */

function toManuscriptBody(manuscripts: PostRuleBasedManuscriptParam[]) {
  return manuscripts.map(({ manuscriptKey, source }) => ({
    manuscript_key: manuscriptKey,
    ...(source ? { source } : {}),
  }));
}

/**
 * AI校正を投入する（`POST /proofreading/extract-proofreading` → 202）。
 * 同期並列 / Batch の切り替えは BE の実行モードスイッチが持つため
 * `geminiExecution` は送らない（旧 BE では未知キーで 422 になる）。
 */
export async function postExtractProofreading(
  params: PostExtractProofreadingParams,
): Promise<PostExtractProofreadingResponse> {
  const response = await API.post("NolaRestAPI", "/proofreading/extract-proofreading", {
    headers: {
      "Content-Type": "application/json",
    },
    body: {
      novelId: params.novelId,
      manuscripts: toManuscriptBody(params.manuscripts),
      platform: params.platform ?? PROOFREADING_CLIENT_PLATFORM,
      ...(params.checks ? { checks: params.checks } : {}),
    },
  });

  if (
    !response
    || response.status !== "accepted"
    || typeof response.batchId !== "string"
    || typeof response.estimatedFish !== "number"
    || !response.checks
  ) {
    throw new Error("AI校正投入 API のレスポンス形式が不正です。");
  }

  return response as PostExtractProofreadingResponse;
}

export type GetProofreadingResumeResponse = {
  status: "ok";
  novelId: string;
  aiBatch: {
    batchId: string;
    novelId: string;
    batchStatus: AiProofreadingBatchStatus;
    geminiExecution?: GeminiProofreadingExecution;
    usedFish?: number;
    usedRequestToken?: number;
    usedResponseToken?: number;
    createdAt?: number;
    updatedAt?: number;
  } | null;
  work: { manuscriptIds: string[] } | null;
};

/**
 * 当該ユーザー・作品の再開状態を取得する（`GET /proofreading/resume/:novelId`）。
 * batchId を知らなくても RUNNING / COMPLETED を復元できる。
 */
export async function getProofreadingResume(
  novelId: string,
): Promise<GetProofreadingResumeResponse> {
  const response = await API.get(
    "NolaRestAPI",
    `/proofreading/resume/${encodeURIComponent(novelId)}`,
    {},
  );

  if (!response || response.status !== "ok" || typeof response.novelId !== "string") {
    throw new Error("校正再開 API のレスポンス形式が不正です。");
  }
  if (response.aiBatch != null) {
    if (
      typeof response.aiBatch.batchId !== "string"
      || typeof response.aiBatch.batchStatus !== "string"
    ) {
      throw new Error("校正再開 API の aiBatch 形式が不正です。");
    }
  }

  return response as GetProofreadingResumeResponse;
}

/**
 * バッチ状態を取得する（`GET /proofreading/batches/:novelId/:batchId`）。
 */
export async function getProofreadingBatch(
  novelId: string,
  batchId: string,
): Promise<GetProofreadingBatchResponse> {
  const response = await API.get(
    "NolaRestAPI",
    `/proofreading/batches/${encodeURIComponent(novelId)}/${encodeURIComponent(batchId)}`,
    {},
  );

  if (
    !response
    || response.status !== "ok"
    || typeof response.batchId !== "string"
    || typeof response.batchStatus !== "string"
  ) {
    throw new Error("AI校正バッチ取得 API のレスポンス形式が不正です。");
  }

  return response as GetProofreadingBatchResponse;
}

/**
 * バッチ完了後に結果を取得する（`POST /proofreading/batches/:novelId/:batchId/finish`）。
 */
export async function finishProofreadingBatch(
  novelId: string,
  batchId: string,
): Promise<FinishProofreadingBatchResponse> {
  const response = await API.post(
    "NolaRestAPI",
    `/proofreading/batches/${encodeURIComponent(novelId)}/${encodeURIComponent(batchId)}/finish`,
    {
      headers: {
        "Content-Type": "application/json",
      },
      body: {},
    },
  );

  if (
    !response
    || response.status !== "ok"
    || typeof response.batchId !== "string"
    || typeof response.batchStatus !== "string"
  ) {
    throw new Error("AI校正バッチ完了 API のレスポンス形式が不正です。");
  }

  return response as FinishProofreadingBatchResponse;
}

/**
 * 実行中バッチを中止、または未反映の完了結果を破棄する。
 */
export async function cancelProofreadingBatch(
  novelId: string,
  batchId: string,
  options?: { discardCompleted?: boolean },
): Promise<CancelProofreadingBatchResponse> {
  const response = await API.post(
    "NolaRestAPI",
    `/proofreading/batches/${encodeURIComponent(novelId)}/${encodeURIComponent(batchId)}/cancel`,
    {
      headers: {
        "Content-Type": "application/json",
      },
      body: options?.discardCompleted ? { discardCompleted: true } : {},
    },
  );

  if (
    !response
    || response.status !== "ok"
    || typeof response.batchId !== "string"
    || response.batchStatus !== "CANCELLED"
    || typeof response.alreadyCancelled !== "boolean"
    || typeof response.refundedFish !== "number"
  ) {
    throw new Error("AI校正バッチキャンセル API のレスポンス形式が不正です。");
  }

  return response as CancelProofreadingBatchResponse;
}

/**
 * 開始から30分以上 RUNNING のバッチをキャンセルし、消費した Fish を返す。
 */
export async function cancelStaleProofreadingBatch(
  novelId: string,
  batchId: string,
): Promise<CancelStaleProofreadingBatchResponse> {
  const response = await API.post(
    "NolaRestAPI",
    `/proofreading/batches/${encodeURIComponent(novelId)}/${encodeURIComponent(batchId)}/cancel-stale`,
    {
      headers: {
        "Content-Type": "application/json",
      },
      body: {},
    },
  );

  if (
    !response
    || response.status !== "ok"
    || typeof response.batchId !== "string"
    || response.batchStatus !== "CANCELLED"
    || typeof response.alreadyCancelled !== "boolean"
    || typeof response.refundedFish !== "number"
    || typeof response.elapsedMs !== "number"
  ) {
    throw new Error("AI校正の返金キャンセル API のレスポンス形式が不正です。");
  }

  return response as CancelStaleProofreadingBatchResponse;
}

/**
 * AI校正の Fish 見積り（`POST /proofreading/estimate-fish` + `kind: "ai"`）。
 */
export async function estimateAiProofreadingFish(
  params: EstimateAiProofreadingFishParams,
): Promise<EstimateAiProofreadingFishResponse> {
  const response = await API.post("NolaRestAPI", "/proofreading/estimate-fish", {
    headers: {
      "Content-Type": "application/json",
    },
    body: {
      novelId: params.novelId,
      manuscripts: toManuscriptBody(params.manuscripts),
      kind: "ai",
      ...(params.checks ? { checks: params.checks } : {}),
    },
  });

  if (
    !response
    || response.status !== "ok"
    || typeof response.totalChars !== "number"
    || typeof response.withinLimit !== "boolean"
  ) {
    throw new Error("AI校正 Fish 見積り API のレスポンス形式が不正です。");
  }

  return response as EstimateAiProofreadingFishResponse;
}
